/**
 * MediaPicker.jsx
 * Choosing what goes in a post: upload, paste, drop, or reuse something
 * already in the brand's library.
 *
 * Carousels need several items in a deliberate order, so selection is a list
 * rather than a set — the number on each thumbnail is the slide number.
 *
 * Pasting is the fiddly one. A paste arrives on the window, not on any
 * particular element, and the Create screen can have two pickers on it at once
 * — the post's media and a Reel's cover frame. Without a rule about which one
 * takes it, one Ctrl+V would upload the image twice. So pickers register in
 * mount order and the paste goes to whichever you last touched, falling back
 * to the first.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api.js';
import MediaPreview, { formatDuration } from './MediaPreview.jsx';
import { Image as ImageIcon, Warning, Plus, Clipboard, Upload } from './Icons.jsx';
import { filesFromDrop } from '../utils/zip.js';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif';
const ACCEPT = `${IMAGE_ACCEPT},video/mp4,video/quicktime,video/x-m4v`;

// Which pickers are on screen, in mount order, which one you last touched, and
// which still have room. Module-level because a paste arrives on the window and
// has to be resolved across all of them.
const mounted = [];
const hasRoom = new Map();
let lastTouched = null;

/**
 * Which picker should take a pasted file.
 *
 * The one you last touched, if it can still hold anything; otherwise the first
 * that can. A full picker never wins, because "you already have the maximum"
 * is a useless answer when the cover-frame picker beside it was empty.
 */
function takesPaste(id) {
  const roomy = mounted.filter((m) => hasRoom.get(m));
  const chosen =
    (roomy.includes(lastTouched) && lastTouched) ||
    roomy[0] ||
    (mounted.includes(lastTouched) ? lastTouched : mounted[0]);
  return chosen === id;
}

let nextId = 0;

/**
 * How many files one go will upload.
 *
 * Bounded because somebody will eventually choose their Pictures folder, and
 * an unbounded loop there is a very long wait with no way out.
 */
const UPLOAD_LIMIT = 60;

/** "pasted-2026-09-09-01-22-04.png" — legible in the library months later. */
function pastedName(type) {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const ext = (String(type).split('/')[1] || 'png').replace('jpeg', 'jpg').split('+')[0];
  return `pasted-${stamp}.${ext}`;
}

export default function MediaPicker({
  brandId,
  selected,
  max,
  onChange,
  onWarnings,
  onView,
  kind, // 'image' restricts everything to stills — a Reel cover cannot be a video
  label,
}) {
  const fileInput = useRef(null);
  const folderInput = useRef(null);
  // Only shown for a real batch; one file uploads faster than the message
  // would take to read.
  const [progress, setProgress] = useState(null);
  const id = useRef(`picker_${nextId++}`).current;
  const [library, setLibrary] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  // Depth counter, not a boolean: dragging over a child fires leave on the
  // parent, and a boolean flickers.
  const [dragDepth, setDragDepth] = useState(0);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const { media } = await api.media.list(brandId);
      setLibrary(kind ? media.filter((m) => m.kind === kind) : media);
    } catch (e) {
      setError(e);
    }
  }, [brandId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  // Not in React's attribute list, so it has to be set on the element itself.
  // Both spellings: `directory` is the older one and some builds still want it.
  useEffect(() => {
    if (folderInput.current) {
      folderInput.current.setAttribute('webkitdirectory', '');
      folderInput.current.setAttribute('directory', '');
    }
  }, []);

  /**
   * Takes files from wherever they came — the file dialog, a drop, or a paste
   * — and uploads what this picker can actually use.
   *
   * Anything of the wrong kind is named rather than silently dropped: a video
   * dragged onto a cover-frame picker should say why it was refused.
   */
  async function upload(incoming) {
    const files = Array.from(incoming ?? []);
    if (files.length === 0) return;

    const wanted = files.filter(fits);
    const refused = files.filter((f) => !fits(f));

    const room = Math.max(0, max - selected.length);

    // Everything gets uploaded; only as many as fit get attached to the post.
    //
    // This used to take the first one and throw the rest away, which made
    // choosing a folder for a single-image post pointless — thirty-nine files
    // picked, examined, and discarded. They now land in the library, where
    // "Choose from library" can reach them. Nothing selected is ever wasted.
    const keeping = wanted.slice(0, UPLOAD_LIMIT);

    const notes = [];
    for (const f of refused) {
      notes.push(
        kind === 'image'
          ? `${f.name || 'That file'} is not an image, and this only takes images.`
          : `${f.name || 'That file'} is not an image or video Kreeboom can use.`,
      );
    }
    if (wanted.length > UPLOAD_LIMIT) {
      notes.push(
        `${UPLOAD_LIMIT} at a time is the most this will take, so ` +
          `${wanted.length - UPLOAD_LIMIT} were left out.`,
      );
    }

    if (keeping.length === 0) {
      setError(notes.length ? new Error(notes.join(' ')) : null);
      return;
    }

    setBusy(true);
    setError(null);
    const warnings = [...notes];
    const uploaded = [];

    try {
      for (const [i, file] of keeping.entries()) {
        setProgress(keeping.length > 1 ? { done: i + 1, total: keeping.length } : null);
        const res = await api.media.upload(brandId, file);
        uploaded.push(res.media);
        warnings.push(...(res.warnings ?? []));
      }

      const attaching = uploaded.slice(0, room);
      const spare = uploaded.length - attaching.length;

      if (spare > 0) {
        warnings.push(
          room === 0
            ? `${spare} added to the library. This format already has as many as it takes, so ` +
              'none were attached — they are under Choose from library.'
            : `${attaching.length} attached to this post. The other ${spare} are in the ` +
              'library, under Choose from library.',
        );
        // Opened rather than described: the spare pictures are the whole
        // point of having uploaded them, and a sentence saying where they
        // went is weaker than showing them.
        setShowLibrary(true);
      }

      if (attaching.length > 0) onChange([...selected, ...attaching]);
      onWarnings?.(warnings);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setProgress(null);
      setBusy(false);
    }
  }

  /**
   * Every file on a clipboard, however the browser chose to expose it.
   *
   * `files` is the modern list and usually enough — but some sources populate
   * only `items`, and a file whose type the OS did not label arrives with an
   * empty `type`. Both are handled, because "paste sometimes does nothing" is
   * a miserable thing to debug from the outside.
   */
  function filesFrom(clipboard) {
    if (!clipboard) return [];

    const out = [...(clipboard.files ?? [])];

    for (const item of clipboard.items ?? []) {
      if (item.kind !== 'file') continue;
      const file = item.getAsFile?.();
      // Same file can appear in both lists; match on name and size.
      if (file && !out.some((f) => f.name === file.name && f.size === file.size)) out.push(file);
    }

    return out.filter((f) => f && f.size > 0);
  }

  /** Whether this picker can take that file at all. */
  function fits(file) {
    const type = file.type || '';

    // Some clipboard and drag sources hand over a file with no MIME type at
    // all. Falling back to the extension keeps those usable rather than
    // rejecting them for a label the OS forgot to attach.
    const byName = /\.(jpe?g|png|webp|gif|avif|mp4|mov|m4v)$/i.exec(file.name ?? '');
    const guessed = byName ? (/\.(mp4|mov|m4v)$/i.test(file.name) ? 'video/' : 'image/') : '';
    const effective = type || guessed;

    if (kind === 'image') return effective.startsWith('image/');
    return effective.startsWith('image/') || effective.startsWith('video/');
  }

  /**
   * Reads the clipboard on demand, for the Paste button.
   *
   * Ctrl+V depends on the page having keyboard focus, which is exactly the
   * thing that goes wrong after switching from a screenshot tool. A button
   * asks the browser directly and does not care where the cursor is.
   */
  async function pasteFromClipboard() {
    if (!navigator.clipboard?.read) {
      setError(new Error('This browser will not let a page read the clipboard. Press Ctrl+V instead.'));
      return;
    }

    try {
      const items = await navigator.clipboard.read();
      const files = [];

      for (const item of items) {
        const type = item.types.find((t) => t.startsWith('image/') || t.startsWith('video/'));
        if (!type) continue;
        const blob = await item.getType(type);
        files.push(new File([blob], pastedName(type), { type }));
      }

      if (files.length === 0) {
        setError(new Error('There is no image on the clipboard. Copy one, then press Paste.'));
        return;
      }

      lastTouched = id;
      await upload(files);
    } catch (e) {
      // Chrome refuses without permission, and Firefox refuses outright.
      setError(
        new Error(
          `Could not read the clipboard (${e.message}). Press Ctrl+V with this page in focus instead.`,
        ),
      );
    }
  }

  // ---------- paste ----------
  useEffect(() => {
    if (!mounted.includes(id)) mounted.push(id);
    hasRoom.set(id, selected.length < max);
    if (lastTouched === null) lastTouched = id;

    function onPaste(e) {
      const files = filesFrom(e.clipboardData);

      // Pasted text is somebody else's business — a caption box, most likely —
      // and this never sees it, because there are no files on the clipboard.
      if (files.length === 0) return;

      // Files, though, are taken wherever the cursor happens to be. A caption
      // box cannot do anything with an image, so a screenshot pasted while
      // writing would otherwise vanish with no explanation.
      if (!takesPaste(id)) return;

      e.preventDefault();
      // A screenshot arrives named "image.png" or nothing at all; a useful
      // name makes the library legible later.
      upload(
        files.map((f) =>
          f.name && f.name !== 'image.png' ? f : new File([f], pastedName(f.type), { type: f.type }),
        ),
      );
    }

    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('paste', onPaste);
      const at = mounted.indexOf(id);
      if (at >= 0) mounted.splice(at, 1);
      hasRoom.delete(id);
      if (lastTouched === id) lastTouched = mounted[0] ?? null;
    };
    // `upload` closes over the current selection, so the listener is refreshed
    // when that changes — otherwise a paste would append to a stale list.
  });

  // ---------- drag and drop ----------
  const dragProps = {
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDragDepth((d) => d + 1);
    },
    onDragOver: (e) => {
      // Without this the browser opens the file instead of letting us have it.
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    },
    onDragLeave: () => setDragDepth((d) => Math.max(0, d - 1)),
    onDrop: async (e) => {
      e.preventDefault();
      setDragDepth(0);
      lastTouched = id;
      // A dropped folder has no `files` to speak of — it arrives as a
      // FileSystemEntry that has to be walked. The same walker the Resize
      // screen uses, so a folder behaves identically in both places.
      const found = await filesFromDrop(e.dataTransfer?.items ?? [], { maxFiles: max });
      upload(found.length ? found : e.dataTransfer?.files);
    },
  };

  function toggle(item) {
    const at = selected.findIndex((m) => m.id === item.id);
    if (at >= 0) {
      onChange(selected.filter((m) => m.id !== item.id));
      return;
    }
    if (selected.length >= max) return;
    onChange([...selected, item]);
  }

  const full = selected.length >= max;

  const dragging = dragDepth > 0;

  return (
    <div
      className={`field picker-root${dragging ? ' is-dropping' : ''}`}
      onPointerDown={() => {
        lastTouched = id;
      }}
      {...dragProps}
    >
      <label>
        {label ?? (max === 1 ? 'Image or video' : 'Images and video')}
        {max > 1 && (
          <span className="counter">
            {selected.length} of {max}
          </span>
        )}
      </label>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {/* ---------- What is chosen, in order ---------- */}
      {selected.length > 0 && (
        <div className="picked">
          {selected.map((m, i) => (
            <div className="picked__item" key={m.id}>
              <MediaPreview
                media={m}
                className="picked__thumb zoomable"
                onClick={() => onView?.(m)}
              />
              {max > 1 && <span className="picked__index">{i + 1}</span>}
              <button
                className="picked__remove"
                onClick={() => onChange(selected.filter((x) => x.id !== m.id))}
                title="Remove"
                type="button"
              >
                ×
              </button>
              <div className="picked__meta">
                {m.kind === 'video'
                  ? `Video ${formatDuration(m.duration_seconds)}`
                  : `${m.width}×${m.height}`}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected.length === 0 && max > 0 && (
        <button
          className={`dropzone${dragging ? ' is-dropping' : ''}`}
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          <ImageIcon size={26} />
          <span>
            {busy
              ? 'Uploading…'
              : dragging
                ? 'Drop it here'
                : kind === 'image'
                  ? 'Choose an image, or drop or paste one'
                  : 'Choose an image or video, or drop or paste one'}
          </span>
          <small>
            {kind === 'image'
              ? 'JPEG, PNG, WebP or GIF · drag a file in, or press Ctrl+V'
              : 'JPEG, PNG, WebP, GIF · MP4 or MOV up to 95MB · drag in, or press Ctrl+V'}
          </small>
        </button>
      )}

      {progress && (
        <p className="hint">
          Uploading {progress.done} of {progress.total}…
        </p>
      )}

      {max > 0 && (
        <div className="btn-row" style={{ marginTop: 10 }}>
          <button
            className="btn btn--sm"
            onClick={() => fileInput.current?.click()}
            disabled={busy || full}
          >
            <Plus size={15} /> {busy ? 'Uploading…' : 'Upload'}
          </button>
          {/* Offered whatever the format, and even when the post is full:
              everything in the folder reaches the library either way. */}
          <button
            className="btn btn--sm"
            onClick={() => folderInput.current?.click()}
            disabled={busy}
            title="Add every picture in a folder to the library"
          >
            <Upload size={15} /> Folder
          </button>
          <button
            className="btn btn--sm"
            onClick={pasteFromClipboard}
            disabled={busy || full}
            title="Paste an image you have copied"
          >
            <Clipboard size={15} /> Paste
          </button>
          <button
            className="btn btn--sm"
            onClick={() => setShowLibrary((v) => !v)}
            disabled={full && !showLibrary}
          >
            {showLibrary ? 'Hide library' : 'Choose from library'}
          </button>
          {full ? (
            <span className="hint">That is the maximum for this format.</span>
          ) : (
            selected.length > 0 && (
              <span className="hint">
                {dragging
                  ? 'Drop to add it'
                  : max > 1
                    ? 'Or drag files or a folder in, or press Ctrl+V'
                    : 'Or drag one in, or press Ctrl+V'}
              </span>
            )
          )}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept={kind === 'image' ? IMAGE_ACCEPT : ACCEPT}
        multiple={max > 1}
        hidden
        onChange={(e) => upload(Array.from(e.target.files ?? []))}
      />

      {/* A folder picker returns everything inside, including files that are
          not pictures at all — a Windows folder has a Thumbs.db in it. Those
          are filtered out silently here rather than reported, because "your
          folder contained a desktop.ini" is not a useful thing to be told. */}
      <input
        ref={folderInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const all = Array.from(e.target.files ?? []);
          upload(all.filter(fits));
          // Cleared so choosing the same folder twice fires again.
          e.target.value = '';
        }}
      />

      {/* ---------- The brand's library ---------- */}
      {showLibrary && (
        <div className="library" style={{ marginTop: 14 }}>
          {library.length === 0 && <p className="hint">Nothing in the library yet.</p>}
          {library.map((m) => {
            const at = selected.findIndex((x) => x.id === m.id);
            return (
              <button
                key={m.id}
                type="button"
                className={`lib-item${at >= 0 ? ' is-picked' : ''}`}
                onClick={() => toggle(m)}
              >
                <MediaPreview media={m} />
                <div className="lib-item__meta">
                  <span>
                    {m.kind === 'video'
                      ? `Video ${formatDuration(m.duration_seconds)}`
                      : `${m.width}×${m.height}`}
                  </span>
                  <span>{at >= 0 ? `#${at + 1}` : 'use'}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
